<template>
  <div>
    <v-toolbar dense flat color="white">
      <v-spacer />
      <span class="order-text-pagename elevation-2">Rider Tracker</span>
      <v-spacer />
    </v-toolbar>
    <div class="mt-3 pa-2">
      <v-layout wrap>
        <v-btn
          class="location-btn"
          @click="setCenter(location)"
          color="black"
          style="color: white"
          v-for="(location, index) in Object.keys(provinceCoordinates)"
          :key="index"
          >{{ location }}</v-btn
        >
      </v-layout>
    </div>
    <div id="map" ref="mapRef" class="mt-5">
      <div v-if="loading" class="loading">Loading riders...</div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      ws: null,
      map: null,
      markers: new Map(),
      mapLoaded: false,
      loading: true,
      isModalVisible: false,
      formData: {
        riderId: "",
        latitude: null,
        longitude: null,
      },
      newRider: {
        riderId: "",
        name: "",
      },
      ridersList: [],
      selectedRiderId: "",
      locations: {
        thailand: { lat: 13.7563, lng: 100.5018, zoom: 6 }, // Bangkok coordinates as default center
      },
      provinceCoordinates: {
        phuket: { lat: 7.8804, lng: 98.3923, zoom: 12 },
        suratthani: { lat: 9.1351, lng: 99.3332, zoom: 11 },
        krabi: { lat: 8.0863, lng: 98.9063, zoom: 11 },
        trang: { lat: 7.5645, lng: 99.6239, zoom: 11 },
        songkhla: { lat: 7.1896, lng: 100.5953, zoom: 11 },
        yala: { lat: 6.5413, lng: 101.2803, zoom: 11 },
        prachuapkhirikhan: { lat: 11.8126, lng: 99.7957, zoom: 11 },
        nakhonsithammarat: { lat: 8.4304, lng: 99.9633, zoom: 11 },
        pattani: { lat: 6.8692, lng: 101.255, zoom: 11 },
        ranong: { lat: 9.9529, lng: 98.6085, zoom: 11 },
        phatthalung: { lat: 7.6166, lng: 100.0742, zoom: 11 },
        satun: { lat: 6.6238, lng: 100.0675, zoom: 11 },
        phangnga: { lat: 8.4511, lng: 98.5292, zoom: 11 },
        phetchaburi: { lat: 13.1119, lng: 99.9406, zoom: 11 },
        ratchaburi: { lat: 13.5282, lng: 99.8134, zoom: 11 },
        chiangmai: { lat: 18.7883, lng: 98.9853, zoom: 11 },
        chiangrai: { lat: 19.9105, lng: 99.8403, zoom: 11 },
        pathumthani: { lat: 14.0208, lng: 100.5253, zoom: 11 },
        narathiwat: { lat: 6.4215, lng: 101.8235, zoom: 11 },
        lamphun: { lat: 18.5743, lng: 99.0087, zoom: 11 },
        buriram: { lat: 14.9951, lng: 103.1116, zoom: 11 },
      },
      googleMapsLoaded: false,
    };
  },
  created() {},
  async mounted() {
    await this.loadGoogleMaps();
    this.initWebSocket();
    this.initMap();
  },
  beforeUnmount() {
    if (this.ws) {
      this.ws.close();
    }
  },
  beforeDestroy() {
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  },
  methods: {
    loadGoogleMaps() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          this.googleMapsLoaded = true;
          resolve();
          return;
        }

        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAIBSRxCOHKfzMqh5QV8Er6_tRYNFTudTE&libraries=marker&v=beta`;
        script.async = true;
        script.defer = true;

        script.onload = () => {
          this.googleMapsLoaded = true;
          resolve();
        };

        script.onerror = (err) => {
          reject(new Error("Failed to load Google Maps"));
        };

        document.head.appendChild(script);
      });
    },

    async initMap() {
      if (!this.googleMapsLoaded) {
        console.error("Google Maps not loaded");
        return;
      }

      const initialCenter = this.locations.thailand;

      this.map = new google.maps.Map(this.$refs.mapRef, {
        center: initialCenter,
        zoom: initialCenter.zoom,
        mapId: "DEMO_MAP_ID",
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });

      this.map.addListener("click", (e) => {
        if (this.selectedRiderId) {
          this.ws.send(
            JSON.stringify({
              type: "UPDATE_LOCATION",
              riderId: this.selectedRiderId,
              lat: e.latLng.lat(),
              lng: e.latLng.lng(),
            })
          );
        }
      });

      await new Promise((resolve) => {
        this.map.addListener("tilesloaded", () => {
          this.mapLoaded = true;
          resolve();
        });
      });

      this.ws.send(JSON.stringify({ type: "GET_RIDERS" }));
    },

    initWebSocket() {
      this.ws = new WebSocket("wss://rider-tracker-ws.weserve.asia/ws");

      // Heartbeat setup
      const pingInterval = setInterval(() => {
        if (this.ws.readyState === WebSocket.OPEN) {
          this.ws.send("ping");
        }
      }, 30000); // Send ping every 30 seconds

      this.ws.onclose = (event) => {
        clearInterval(pingInterval);
        console.log("Connection closed:", event.code, event.reason);
        // Implement reconnection logic here
      };

      this.ws.onmessage = (event) => {
        if (event.data === "pong") {
          console.log("Received server heartbeat");
          return;
        }

        try {
          const data = JSON.parse(event.data);
          switch (data.type) {
            case "RIDERS_LIST":
              this.loading = false;
              this.markers.forEach((marker) => marker.setMap(null));
              this.markers.clear();
              const bounds = new google.maps.LatLngBounds();
              this.ridersList = data.riders; // Store riders list

              data.riders.forEach((rider) => {
                this.updateRiderMarker(rider);
                bounds.extend(new google.maps.LatLng(rider.lat, rider.lng));
              });
              break;
            case "RIDER_LOCATION":
              this.updateRiderMarker(data);
              break;
            case "RIDER_STATUS":
              this.updateRiderMarker(data);
              break;
            case "ERROR":
              alert(`Error: ${data.message}`);
              break;
          }
        } catch (error) {
          console.error("Error parsing WebSocket message:", error);
        }
      };

      this.ws.onopen = () => {
        console.log("Connected to server");
        if (this.mapLoaded) {
          this.ws.send(JSON.stringify({ type: "GET_RIDERS" }));
        }
      };

      this.ws.onerror = (error) => {
        console.error("WebSocket error:", error);
        this.loading = false;
      };
    },

    formatDate(date) {
      return new Date(date).toLocaleString();
    },

    createInfoWindow(rider) {
      return new google.maps.InfoWindow({
        content: `
          <div class="info-window">
            <h4>${rider.name}</h4>
            <p><strong>ID:</strong> ${rider.riderId}</p>
          
            <p><strong>Location:</strong><br>
            Lat: ${rider.lat.toFixed(6)}<br>
            Lng: ${rider.lng.toFixed(6)}</p>
            <p><strong>Last Updated:</strong><br>${this.formatDate(
              rider.lastUpdated
            )}</p>
          </div>
        `,
      });
    },

    createCustomMarker(position, rider) {
      const markerColor =
        rider.status.toLowerCase() === "active" ? "#4CAF50" : "#f44336";

      // Check if AdvancedMarkerElement is available
      if (google.maps.marker && google.maps.marker.AdvancedMarkerElement) {
        const content = document.createElement("div");
        content.className = "price-tag";
        content.style.backgroundColor = markerColor;
        content.textContent = rider.name || `Rider ${rider.riderId}`;

        return new google.maps.marker.AdvancedMarkerElement({
          position,
          content,
          map: this.map,
          title: rider.name || `Rider ${rider.riderId}`,
        });
      } else {
        // Fallback to regular marker
        return new google.maps.Marker({
          position,
          map: this.map,
          title: rider.name || `Rider ${rider.riderId}`,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            fillColor: markerColor,
            fillOpacity: 1,
            strokeWeight: 1,
            scale: 8,
          },
        });
      }
    },

    updateRiderMarker(data) {
      const position = { lat: data.lat, lng: data.lng };

      if (this.markers.has(data.riderId)) {
        const marker = this.markers.get(data.riderId);
        this.animateMarkerTo(marker, position);

        // Update marker content
        const content = marker.content;
        content.style.backgroundColor =
          data.status.toLowerCase() === "active" ? "#4CAF50" : "#f44336";
        content.textContent = data.name || `Rider ${data.riderId}`;

        if (marker.infoWindow) {
          // Add null check
          const infoWindow = marker.infoWindow;
          infoWindow.setContent(this.createInfoWindow(data).getContent());
          if (marker.infoWindowOpen) {
            infoWindow.open(this.map, marker);
          }
        }
      } else {
        const marker = this.createCustomMarker(position, data);
        const infoWindow = this.createInfoWindow(data);
        marker.infoWindow = infoWindow;
        marker.infoWindowOpen = false;

        marker.addListener("click", () => {
          this.markers.forEach((m) => {
            m.infoWindow.close();
            m.infoWindowOpen = false;
          });
          infoWindow.open(this.map, marker);
          marker.infoWindowOpen = true;
        });

        this.markers.set(data.riderId, marker);
      }
    },

    animateMarkerTo(marker, newPosition) {
      const frames = 60;
      const duration = 1000;

      // Check if marker is AdvancedMarkerElement
      const isAdvancedMarker =
        marker.constructor.name === "AdvancedMarkerElement";

      const start = {
        lat: isAdvancedMarker
          ? marker.position.lat
          : marker.getPosition().lat(),
        lng: isAdvancedMarker
          ? marker.position.lng
          : marker.getPosition().lng(),
      };

      const deltaLat = (newPosition.lat - start.lat) / frames;
      const deltaLng = (newPosition.lng - start.lng) / frames;
      let frame = 0;

      const animate = () => {
        frame++;
        const lat = start.lat + deltaLat * frame;
        const lng = start.lng + deltaLng * frame;

        if (isAdvancedMarker) {
          marker.position = { lat, lng };
        } else {
          marker.setPosition({ lat, lng });
        }

        if (frame < frames) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    },

    showAddRiderModal() {
      this.isModalVisible = true;
    },

    closeModal() {
      this.isModalVisible = false;
      this.newRider = { riderId: "", name: "" };
    },

    addNewRider() {
      if (!this.newRider.riderId || !this.newRider.name) {
        alert("Please fill all fields");
        return;
      }

      const center = this.map.getCenter();
      this.ws.send(
        JSON.stringify({
          type: "ADD_RIDER",
          riderId: this.newRider.riderId,
          name: this.newRider.name,
          lat: center.lat(),
          lng: center.lng(),
        })
      );

      this.closeModal();
    },

    setCenter(location) {
      const coords = this.provinceCoordinates[location];
      if (coords && this.map) {
        this.map.setCenter(coords);
        this.map.setZoom(coords.zoom);
      }
    },
  },

  watch: {
    // Add watcher for route changes
    "$route.params.province"(newProvince) {
      if (newProvince && this.map) {
        const province = newProvince.toLowerCase();
        const coordinates = this.provinceCoordinates[province];
        if (coordinates) {
          this.map.setCenter(coordinates);
          this.map.setZoom(coordinates.zoom);
        }
      }
    },
  },
};
</script>

<style>
/* Map styles */
#map {
  height: 600px;
  width: 100%;
  margin-bottom: 20px;
  position: relative;
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(255, 255, 255, 0.8);
  padding: 10px 20px;
  border-radius: 4px;
  z-index: 1000;
}

.location-btn {
  padding: 8px 15px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
}

.location-btn:hover {
  background-color: #1976d2;
}

/* Marker styles */
.price-tag {
  background-color: #4285f4;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  padding: 10px 15px;
  position: relative;
}

.price-tag::after {
  content: "";
  position: absolute;
  left: 50%;
  top: 100%;
  transform: translate(-50%, 0);
  width: 0;
  height: 0;
  border-left: 8px solid transparent;
  border-right: 8px solid transparent;
  border-top: 8px solid #4285f4;
}

.price-tag[style*="background-color: #4CAF50"]::after {
  border-top-color: #4caf50;
}

.price-tag[style*="background-color: #f44336"]::after {
  border-top-color: #f44336;
}
</style>
